import { MAX_FILE_BYTES } from '@flowshare/protocol';
import { randomHex, sanitizeFileName } from '@flowshare/shared';

const ROOT_DIRECTORY = 'flowshare';
const RECEIVED_DIRECTORY = 'received';
const MEMORY_FALLBACK_LIMIT = 128 * 1024 * 1024;

export interface ReceiveWriter {
  readonly key: string;
  write: (bytes: Uint8Array) => Promise<void>;
  close: () => Promise<void>;
  abort: () => Promise<void>;
}

interface MemoryRecord {
  chunks: BlobPart[];
  size: number;
}

const memoryFiles = new Map<string, MemoryRecord>();

function storageKeyFor(name: string): string {
  return `${Date.now().toString(36)}-${randomHex(4)}-${sanitizeFileName(name, 'file')}`;
}

async function receivedDirectory(): Promise<FileSystemDirectoryHandle> {
  if (typeof navigator === 'undefined' || !navigator.storage?.getDirectory) {
    throw new Error('OPFS_UNSUPPORTED');
  }
  const root = await navigator.storage.getDirectory();
  const appRoot = await root.getDirectoryHandle(ROOT_DIRECTORY, { create: true });
  return appRoot.getDirectoryHandle(RECEIVED_DIRECTORY, { create: true });
}

class OpfsWriter implements ReceiveWriter {
  private closed = false;

  constructor(
    readonly key: string,
    private readonly handle: FileSystemFileHandle,
    private readonly writable: FileSystemWritableFileStream,
  ) {}

  async write(bytes: Uint8Array): Promise<void> {
    // The Workers/browser typings narrow ArrayBufferView to ArrayBuffer-backed
    // views, while File.slice() can produce ArrayBufferLike. The runtime accepts
    // both, and we never pass a SharedArrayBuffer.
    await this.writable.write(bytes as unknown as FileSystemWriteChunkType);
  }

  async close(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    await this.writable.close();
  }

  async abort(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    try {
      await this.writable.abort();
    } catch {
      // Already closed.
    }
    try {
      const directory = await receivedDirectory();
      await directory.removeEntry(this.key);
    } catch {
      // Best effort cleanup.
    }
    void this.handle;
  }
}

class MemoryWriter implements ReceiveWriter {
  private readonly record: MemoryRecord = { chunks: [], size: 0 };
  private closed = false;

  constructor(readonly key: string) {
    memoryFiles.set(key, this.record);
  }

  async write(bytes: Uint8Array): Promise<void> {
    if (this.record.size + bytes.byteLength > MEMORY_FALLBACK_LIMIT) {
      throw new Error(
        'This browser cannot store a file this large. Enable OPFS or free some space.',
      );
    }
    // Copy: the underlying DataChannel buffer can be reused by the runtime.
    this.record.chunks.push(bytes.slice().buffer);
    this.record.size += bytes.byteLength;
  }

  async close(): Promise<void> {
    this.closed = true;
  }

  async abort(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    memoryFiles.delete(this.key);
  }
}

/**
 * Create an incremental writer for a received file. OPFS keeps memory flat even
 * for very large files; browsers without it fall back to a bounded in-memory
 * buffer (max 512 MB) so the feature still works.
 */
export async function createReceiveWriter(name: string, size: number): Promise<ReceiveWriter> {
  if (size > MAX_FILE_BYTES) throw new Error('FILE_TOO_LARGE');
  const key = storageKeyFor(name);

  try {
    const directory = await receivedDirectory();
    const handle = await directory.getFileHandle(key, { create: true });
    const writable = await handle.createWritable();
    return new OpfsWriter(key, handle, writable);
  } catch {
    return new MemoryWriter(key);
  }
}

function triggerBrowserDownload(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = sanitizeFileName(name, 'flowshare-download');
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
}

export async function downloadStoredFile(storageKey: string, name: string): Promise<boolean> {
  const memory = memoryFiles.get(storageKey);
  if (memory) {
    triggerBrowserDownload(new Blob(memory.chunks), name);
    return true;
  }

  try {
    const directory = await receivedDirectory();
    const handle = await directory.getFileHandle(storageKey);
    const file = await handle.getFile();
    triggerBrowserDownload(file, name);
    return true;
  } catch {
    return false;
  }
}

export async function readStoredFile(storageKey: string): Promise<File | null> {
  try {
    const directory = await receivedDirectory();
    const handle = await directory.getFileHandle(storageKey);
    return await handle.getFile();
  } catch {
    return null;
  }
}

export async function deleteStoredFile(storageKey: string): Promise<void> {
  memoryFiles.delete(storageKey);
  try {
    const directory = await receivedDirectory();
    await directory.removeEntry(storageKey);
  } catch {
    // Nothing to delete.
  }
}

export async function clearReceivedFiles(): Promise<void> {
  memoryFiles.clear();
  try {
    const directory = await receivedDirectory();
    // @ts-expect-error - async iteration over directory entries is supported in modern browsers.
    for await (const [name] of directory.entries()) {
      await directory.removeEntry(name as string);
    }
  } catch {
    // OPFS unavailable or directory missing.
  }
}

export interface StorageEstimate {
  usage: number;
  quota: number;
  supported: boolean;
}

export async function receivedStorageEstimate(): Promise<StorageEstimate> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
    return { usage: 0, quota: 0, supported: false };
  }
  const estimate = await navigator.storage.estimate();
  return {
    usage: estimate.usage ?? 0,
    quota: estimate.quota ?? 0,
    supported: true,
  };
}
